import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import appConstants from "../constants/appConstants";
import Layout from "../components/Layout";
import { FaSave, FaArrowLeft, FaUser } from "react-icons/fa";

const getAuthToken = () => localStorage.getItem("authToken");

const CreateStudent = () => {
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        application_no: "",
        date_of_application: new Date().toISOString().split("T")[0], // Default to today
        admission_no: "",
        date_of_admission: "",
        id_card_no: "",
        name: "",
        address: "",
        gender: "Male",
        dob: "",
        parents: "",
        mobile: "",
        batch: "",
        remark: "",
        parent_access_code: "",
        status: "Active",
    });
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    // Fetch batches on component mount
    useEffect(() => {
        fetchBatches();
    }, []);

    const fetchBatches = async () => {
        const token = getAuthToken();
        if (!token) {
            alert("Unauthorized! Please log in.");
            navigate("/login");
            return;
        }

        try {
            const response = await axios.post(
                `${appConstants.API_ROOT_URL}get-batches`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.status === "success") {
                setBatches(response.data.data);
            } else {
                alert("Failed to load batches.");
            }
        } catch (err) {
            alert("Error fetching batches.");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = getAuthToken();
    
        if (!token) {
            alert("Unauthorized! Please log in.");
            navigate("/login");
            return;
        }
    
        try {
            setLoading(true);
    
            // Prepare form data
            const formDataToSend = new FormData();
            Object.keys(formData).forEach((key) => {
                formDataToSend.append(key, formData[key]);
            });
    
            // Append the photo file (if available)
            if (photo) {
                formDataToSend.append("photo", photo);
            }
    
            const response = await axios.post(
                `${appConstants.API_ROOT_URL}create-student`,
                formDataToSend,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data"
                    }
                }
            );
    
            if (response.data.status === "success") {
                const applicationNo = response.data.data.application_no;
                alert(`Student created successfully! Application No: ${applicationNo}`);
    
                // Ask if the user wants to admit the student
                if (window.confirm("Do you want to admit this student now?")) {
                    await handleAdmit(applicationNo);
                }
    
                navigate(-1);
            } else {
                alert(response.data.message || "Failed to create student.");
            }
        } catch (err) {
            alert(err.response?.data?.message || "Error creating student.");
        } finally {
            setLoading(false);
        }
    };
    

    const handleAdmit = async (application_no) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(
                `${appConstants.API_ROOT_URL}admit-student`,
                { application_no },
                { headers: { Authorization: `Bearer ${token}` } }
            );
    
            if (response.data.status === "success") {
                alert(`Student admitted successfully! Admission No: ${response.data.admission_no}`);
            } else {
                alert(response.data.message);
            }
        } catch (err) {
            alert(err.response?.data?.message || "Error admitting student.");
        }
    };
    
    

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhoto(file);
            setPhotoPreview(URL.createObjectURL(file)); // Show preview
        }
    };

    return (
        <Layout>
            <div className="container mt-4">
                
                {/* Title & Buttons Section */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <FaUser size={28} className="me-2 text-primary" />
                        <h3 className="m-0">Create Student</h3>
                    </div>

                    <div className="d-flex">
                        <button className="btn btn-secondary d-flex align-items-center me-2" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-2" /> Back
                        </button>

                        <button type="submit" className="btn btn-success d-flex align-items-center" onClick={handleSubmit} disabled={loading}>
                                <FaSave className="me-2" /> {loading ? "Saving..." : "Save Student"}
                            </button>
                    </div>
                </div>

                
                <div className="card p-4 shadow">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            {/* Left Column */}
                            <div className="col-md-6">
                                {/* Application Details */}
                                <div className="mb-3">
                                    <label className="form-label">Date of Application</label>
                                    <input type="date" className="form-control" name="date_of_application" value={formData.date_of_application} onChange={handleChange} />
                                </div>
                                {/* Student Info */}
                                <div className="mb-3">
                                    <label className="form-label">ID Card No</label>
                                    <input type="text" className="form-control" name="id_card_no" value={formData.id_card_no} onChange={handleChange} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Name</label>
                                    <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Gender</label>
                                    <select className="form-control" name="gender" value={formData.gender} onChange={handleChange}>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Date of Birth</label>
                                    <input type="date" className="form-control" name="dob" value={formData.dob} onChange={handleChange} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Parents</label>
                                    <textarea className="form-control" name="parents" value={formData.parents} onChange={handleChange}></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Mobile</label>
                                    <input type="text" className="form-control" name="mobile" value={formData.mobile} onChange={handleChange} />
                                </div>
                            </div>
    
                            {/* Right Column */}
                            <div className="col-md-6">
                                {/* Photo Upload */}
                                <div className="mb-3">
                                    <label className="form-label">Upload Photo</label>
                                    <input 
                                        type="file" 
                                        className="form-control" 
                                        accept="image/*" 
                                        ref={fileInputRef} // Attach ref to input
                                        onChange={handlePhotoChange} 
                                    />
                                    
                                    {photoPreview && (
                                        <div className="mt-3">
                                            <img src={photoPreview} alt="Preview" className="img-thumbnail" width="150" />
                                            <button 
                                                type="button" 
                                                className="btn btn-danger btn-sm d-block mt-2"
                                                onClick={() => {
                                                    setPhoto(null);
                                                    setPhotoPreview(null);
                                                    if (fileInputRef.current) {
                                                        fileInputRef.current.value = ""; // Clear file input
                                                    }
                                                }}
                                            >
                                                Remove Photo
                                            </button>
                                        </div>
                                    )}
                                </div>
    
                                <div className="mb-3">
                                    <label className="form-label">Batch</label>
                                    <select className="form-control" name="batch" value={formData.batch} onChange={handleChange}>
                                        <option value="">Select Batch</option>
                                        {batches.map((batch) => (
                                            <option key={batch.id} value={batch.id}>
                                                {batch.class_name} {batch.division}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Address</label>
                                    <textarea className="form-control" name="address" value={formData.address} onChange={handleChange}></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Remark</label>
                                    <textarea className="form-control" name="remark" value={formData.remark} onChange={handleChange}></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Parent Access Code</label>
                                    <input type="text" className="form-control" name="parent_access_code" value={formData.parent_access_code} readOnly />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Status</label>
                                    <select className="form-control" name="status" value={formData.status} onChange={handleChange}>
                                        <option value="Active">Active</option>
                                        <option value="Suspend">Suspend</option>
                                    </select>
                                </div>
                            </div>
                        </div>
    
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default CreateStudent;
